import { useCallback, useMemo } from 'react';
// import { useSignals } from '@preact/signals-react/runtime';
import { useEffectOnce } from 'react-use';
import jsonData from '../signals/jsonData';
import configData from '../signals/configData';
import useRecord from './useRecord';
import { exhibitorKeyOf, exhibitorsAtBooth, firstExhibitorAtBooth, isSameExhibitor } from '../tools/boothExhibitors';

/**
 * 收藏認的是「哪一家廠商」（序號），不是攤位號碼——同一個號碼底下可能有十幾家，
 * 認號碼的話收藏一家等於整攤都收進去。
 *
 * 客戶那支收藏 API 目前只認 company_booth_number。這裡兩個欄位都送：
 * company_booth_number 讓現在的 API 照常運作，company_serial_number 是請他們改認的那個。
 * 讀回來時先看 company_serial_number，沒有才退回 company_booth_number——
 * 那時同號碼的幾家會一起被標成收藏，這是 API 改之前本來就有的行為，不是這裡弄壞的。
 */
const 伺服器收藏欄位 = ['company_serial_number', 'company_booth_number'];

const 伺服器回傳的值 = (項) => {
	for (const 欄 of 伺服器收藏欄位) {
		const 值 = 項?.[欄];
		if (值 !== undefined && 值 !== null && String(值).trim() !== '') return String(值).trim();
	}
	return '';
};

/** 伺服器給的一個值（序號、或還沒改之前的攤位號碼）對到哪些廠商的 key。 */
const 對到的key = (exhibitorInfo, 值) => {
	if (!值) return [];
	const 直接 = exhibitorInfo.filter((廠商) => exhibitorKeyOf(廠商) === 值);
	if (直接.length) return 直接.map(exhibitorKeyOf);
	return exhibitorsAtBooth(exhibitorInfo, 值).map(exhibitorKeyOf);
};

const 讀本機 = () => {
	try {
		return JSON.parse(localStorage.getItem('data')) || null;
	} catch {
		return null;
	}
};

const 寫本機 = (localData) => localStorage.setItem('data', JSON.stringify(localData));

const useData = () => {
	// useSignals();

	const { exhibitorInfo } = jsonData.value;
	const { userAPI } = configData.value;
	const { makeRecord } = useRecord();

	const visitorParam = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		const visitor = params.get('visitor');
		return visitor;
	}, []);

	const token = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		const token = params.get('vtoken');
		return token;
	}, []);

	useEffectOnce(() => {
		if (visitorParam && token && userAPI && !window.favFetched) {
			window.favFetched = true;
			fetch(`${userAPI}?token=${token}&visitor_id=${visitorParam}`)
				.then(response => response.json())
				.then(data => {
					if (data.message) alert(data.message);
					const 伺服器的 = (Array.isArray(data.data) ? data.data : []).map(伺服器回傳的值);
					const serverFavouriteKeys = [...new Set(伺服器的.flatMap((值) => 對到的key(exhibitorInfo, 值)))];
					const updatedExhibitorInfo = exhibitorInfo.map(exhibitor => ({
						...exhibitor,
						favourite: serverFavouriteKeys.includes(exhibitorKeyOf(exhibitor)),
					}));

					const localData = 讀本機() || {};
					localData.favouriteExhibitors = serverFavouriteKeys;
					寫本機(localData);

					jsonData.value = {
						...jsonData.value,
						exhibitorInfo: updatedExhibitorInfo,
					};
				})
				.catch(error => console.error(error));
		}
	});

	/** 呼叫端給廠商物件；舊的呼叫端給攤位號碼字串也接受，當成那個號碼底下的第一家。 */
	const 認出廠商 = useCallback((target) => (typeof target === 'string' ? firstExhibitorAtBooth(exhibitorInfo, target) : target), [exhibitorInfo]);

	const updateLocalFavourites = useCallback((target) => {
		const 本人 = 認出廠商(target);
		if (!本人) return;
		const key = exhibitorKeyOf(本人);
		let 收藏了 = false;

		const updatedExhibitorInfo = exhibitorInfo.map(exhibitor => {
			if (!isSameExhibitor(exhibitor, 本人)) return exhibitor;
			收藏了 = !exhibitor.favourite;
			return { ...exhibitor, favourite: 收藏了 };
		});

		const localData = 讀本機() || {};
		const 原本 = Array.isArray(localData.favouriteExhibitors) ? localData.favouriteExhibitors : [];
		// 舊版存的是攤位號碼；取消收藏時把它一起拿掉，不然重新整理之後又變回收藏。
		const 拿掉之後 = 原本.filter((值) => 值 !== key && 值 !== 本人['攤位號碼']);
		localData.favouriteExhibitors = 收藏了 ? [...拿掉之後, key] : 拿掉之後;
		寫本機(localData);

		if (收藏了) makeRecord({ Action: 'F', Booth: 本人['攤位號碼'] });

		jsonData.value = {
			...jsonData.value,
			exhibitorInfo: updatedExhibitorInfo,
		};
	}, [exhibitorInfo, makeRecord, 認出廠商]);

	const toggleFavourite = useCallback((target) => {
		const 本人 = 認出廠商(target);
		if (!本人) return;

		if (visitorParam && token && userAPI) {
			const 參數 = [
				`token=${token}`,
				`visitor_id=${visitorParam}`,
				`company_booth_number=${encodeURIComponent(本人['攤位號碼'] || '')}`,
				`company_serial_number=${encodeURIComponent(exhibitorKeyOf(本人))}`,
			].join('&');

			fetch(`${userAPI}?${參數}`, {
				method: 本人.favourite ? 'DELETE' : 'POST',
			})
				.then(response => response.json())
				.then(data => {
					// if (data.message) alert(data.message);
				})
				.catch(error => console.error(error));
		}

		updateLocalFavourites(本人);
	}, [token, updateLocalFavourites, userAPI, visitorParam, 認出廠商]);

	return {
		toggleFavourite,
	};
}

export default useData;
